/**
 * Authentication Store
 *
 * Zustand store for managing authentication and authorization state:
 * - User session management
 * - Token handling (JWT + refresh)
 * - Permission caching
 * - Audit logging (telemetry only - server-side for compliance)
 *
 * @security SEC-001: Role-Based Access Control Implementation
 * @priority P0 CRITICAL
 *
 * Peer Review Completed:
 * - Gemini 3 Pro: 7/10 (architectural concerns addressed below)
 * - GPT-5.1: pending refresh after Sprint 10B changes
 *
 * SECURITY ARCHITECTURE NOTES:
 * ============================
 * 1. Frontend RBAC is ADVISORY ONLY - backend enforces all permissions
 * 2. Access tokens stored in memory only (not localStorage)
 * 3. Refresh tokens should be HttpOnly cookies (configured server-side)
 * 4. All API calls must be validated server-side regardless of frontend checks
 * 5. Client-side audit logs are for UX telemetry, not compliance
 */

import { create } from 'zustand';
import { devtools, persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type {
  User,
  UserRole,
  AuthToken,
  AuthStatus,
  AuthError,
  Permission,
  AuditLogEntry,
  LoginCredentials,
} from '../types/auth';
import {
  getPermissionsForRole,
  hasMinimumRole,
  hasAllPermissions,
  hasAnyPermission,
  ROLE_HIERARCHY,
} from '../types/auth';

// ============================================================================
// Cross-Tab Synchronization (Security Enhancement)
// ============================================================================

/**
 * BroadcastChannel for cross-tab logout synchronization
 * 
 * SECURITY: Prevents zombie sessions where user logs out in Tab A
 * but remains authenticated in Tab B. This is a critical security
 * feature for shared computers/terminals.
 * 
 * @see ISS-002 Security Hardening - Cross-tab session management
 */
const AUTH_CHANNEL_NAME = 'raptor-auth-channel';
let authChannel: BroadcastChannel | null = null;

// Initialize BroadcastChannel (with fallback for unsupported browsers/SSR)
// Check for browser environment first to prevent SSR issues
if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
  try {
    authChannel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    
    // Handle channel errors gracefully
    authChannel.onmessageerror = (e) => {
      if (import.meta.env.DEV) {
        console.error('[Auth] BroadcastChannel message error:', e);
      }
    };
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('[Auth] BroadcastChannel not available, cross-tab sync disabled:', error);
    }
  }
}

// ============================================================================
// Runtime Validation (Security Enhancement)
// ============================================================================

/**
 * Valid user roles for runtime validation
 */
const VALID_ROLES: readonly UserRole[] = Object.freeze([
  'viewer', 'operator', 'analyst', 'security_analyst', 
  'senior_analyst', 'admin', 'super_admin'
]);

/**
 * Runtime validation for User objects
 * 
 * SECURITY: TypeScript types are compile-time only. If the API response
 * is tampered with or malformed, this validates data integrity before
 * it enters the authentication state.
 * 
 * @param user - The user object to validate
 * @returns Validation result with typed user or error
 */
function validateUser(user: unknown): { valid: true; data: User } | { valid: false; errors: string[] } {
  const errors: string[] = [];
  
  if (!user || typeof user !== 'object') {
    return { valid: false, errors: ['User must be an object'] };
  }
  
  const u = user as Record<string, unknown>;
  
  // Required string fields
  if (typeof u.id !== 'string' || u.id.length === 0) {
    errors.push('User ID must be a non-empty string');
  }
  
  // Stricter email validation (check string type first, then format)
  if (typeof u.email !== 'string' || u.email.length < 5 || !u.email.includes('@') || !u.email.includes('.')) {
    errors.push('Email must be a valid email address');
  }
  
  if (typeof u.name !== 'string') {
    errors.push('Name must be a string');
  }
  
  // Role validation - critical for authorization
  if (!VALID_ROLES.includes(u.role as UserRole)) {
    errors.push(`Role must be one of: ${VALID_ROLES.join(', ')}`);
  }
  
  // Date validation
  if (u.createdAt && !(u.createdAt instanceof Date) && isNaN(new Date(u.createdAt as string).getTime())) {
    errors.push('createdAt must be a valid date');
  }
  
  if (errors.length > 0) {
    return { valid: false, errors };
  }
  
  // Return validated user with proper types
  return {
    valid: true,
    data: {
      id: u.id as string,
      email: u.email as string,
      name: u.name as string,
      role: u.role as UserRole,
      createdAt: u.createdAt instanceof Date ? u.createdAt : new Date(u.createdAt as string),
      mfaEnabled: Boolean(u.mfaEnabled),
      lastLoginAt: u.lastLoginAt ? (u.lastLoginAt instanceof Date ? u.lastLoginAt : new Date(u.lastLoginAt as string)) : undefined,
      loginCount: typeof u.loginCount === 'number' ? u.loginCount : undefined,
    }
  };
}

// ============================================================================
// State Interface
// ============================================================================

interface AuthState {
  // User & Session
  user: User | null;
  token: AuthToken | null;
  status: AuthStatus;
  error: AuthError | null;
  hasHydrated: boolean;

  // Permissions
  permissions: Permission[];
  permissionCache: Map<string, boolean>;

  // Audit
  auditQueue: AuditLogEntry[];
  auditFlushInterval: number | null;

  // Session Management
  sessionStartedAt: Date | null;
  lastActivityAt: Date | null;
  sessionTimeoutMs: number;
  tokenRefreshIntervalId: number | null;

  // UI State
  isRefreshing: boolean;
  showMfaDialog: boolean;
  
  // Demo mode (development only)
  demoPasscode: string | null;
}

// ============================================================================
// Actions Interface
// ============================================================================

interface AuthActions {
  // Authentication
  login: (credentials: LoginCredentials) => Promise<boolean>;
  logout: () => void;
  refreshToken: () => Promise<boolean>;
  submitMfaCode: (code: string) => Promise<boolean>;
  
  // Passwordless Authentication (Email + Passcode)
  requestPasscode: (email: string) => Promise<boolean>;
  verifyPasscode: (email: string, passcode: string) => Promise<boolean>;
  clearError: () => void;

  // Session Management
  setUser: (user: User | null) => void;
  setToken: (token: AuthToken | null) => void;
  setStatus: (status: AuthStatus) => void;
  setError: (error: AuthError | null) => void;
  setHasHydrated: (hasHydrated: boolean) => void;
  updateLastActivity: () => void;
  checkSessionExpiry: () => boolean;
  startTokenRefreshTimer: () => void;
  stopTokenRefreshTimer: () => void;

  // Permission Checking
  hasPermission: (permission: Permission) => boolean;
  hasAllPermissions: (permissions: Permission[]) => boolean;
  hasAnyPermission: (permissions: Permission[]) => boolean;
  hasMinimumRole: (role: UserRole) => boolean;
  checkPermission: (permission: Permission, resourceId?: string) => boolean;
  clearPermissionCache: () => void;

  // Audit Logging
  logAuditEvent: (
    permission: Permission,
    allowed: boolean,
    action: string,
    resourceType: string,
    resourceId?: string,
    metadata?: Record<string, unknown>
  ) => void;
  flushAuditQueue: () => Promise<void>;

  // Utilities
  getDisplayRole: () => string;
  canAccessRoute: (requiredPermissions?: Permission[], requiredRole?: UserRole) => boolean;
  reset: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

// Session expiry: 24 hours max for persisted sessions
const MAX_SESSION_AGE_MS = 24 * 60 * 60 * 1000; // 24 hours

const initialState: AuthState = {
  user: null,
  token: null,
  // Start in loading until persist rehydrates; prevents ProtectedRoute from
  // redirecting to /login before localStorage state is applied.
  status: 'loading',
  error: null,
  hasHydrated: false,
  permissions: [],
  permissionCache: new Map(),
  auditQueue: [],
  auditFlushInterval: null,
  sessionStartedAt: null,
  lastActivityAt: null,
  sessionTimeoutMs: 30 * 60 * 1000, // 30 minutes default
  tokenRefreshIntervalId: null,
  isRefreshing: false,
  showMfaDialog: false,
  demoPasscode: null,
};

// ============================================================================
// Store Creation
// ============================================================================

export const useAuthStore = create<AuthState & AuthActions>()(
  devtools(
    persist(
      subscribeWithSelector(
        immer((set, get) => ({
          ...initialState,

          // ----------------------------------------------------------------
          // Authentication Actions
          // ----------------------------------------------------------------

          login: async (credentials: LoginCredentials): Promise<boolean> => {
            set((state) => {
              state.status = 'loading';
              state.error = null;
            });

            try {
              // API call would go here - for now, simulate
              // In production: const response = await authAPI.login(credentials);

              // Simulated response for development
              // In production, this would be replaced with actual API call
              const simulatedUser: User = {
                id: crypto.randomUUID(),
                name: 'Security Admin',
                email: credentials.email,
                role: 'admin', // Would come from server
                createdAt: new Date(),
                mfaEnabled: false,
                lastLoginAt: new Date(),
              };

              const simulatedToken: AuthToken = {
                accessToken: 'simulated_jwt_token',
                refreshToken: 'simulated_refresh_token',
                tokenType: 'Bearer',
                expiresIn: 3600,
                issuedAt: new Date(),
              };

              // Update state
              set((state) => {
                state.user = simulatedUser;
                state.token = simulatedToken;
                state.status = 'authenticated';
                state.permissions = getPermissionsForRole(simulatedUser.role);
                state.sessionStartedAt = new Date();
                state.lastActivityAt = new Date();
              });

              // Start token refresh timer
              get().startTokenRefreshTimer();

              // Log successful login
              get().logAuditEvent(
                'dashboard:view',
                true,
                'login',
                'session',
                simulatedUser.id,
                { method: 'password', rememberMe: credentials.rememberMe }
              );

              return true;
            } catch (error) {
              const authError: AuthError = {
                code: 'invalid_credentials',
                message: error instanceof Error ? error.message : 'Login failed',
              };

              set((state) => {
                state.status = 'unauthenticated';
                state.error = authError;
              });

              return false;
            }
          },

          logout: () => {
            const { stopTokenRefreshTimer } = get();

            // Stop token refresh timer first
            stopTokenRefreshTimer();

            // Clear state in a single atomic update
            set((state) => {
              state.user = null;
              state.token = null;
              state.status = 'unauthenticated';
              state.error = null;
              state.permissions = [];
              state.permissionCache = new Map(); // Create new map instead of clear()
              state.sessionStartedAt = null;
              state.lastActivityAt = null;
              state.auditQueue = []; // Clear audit queue atomically
            });

            /**
             * CROSS-TAB LOGOUT SYNCHRONIZATION
             * 
             * SECURITY: Broadcast logout event to all open tabs to prevent
             * zombie sessions. If user logs out in Tab A, all other tabs
             * (Tab B, C, etc.) should also be logged out immediately.
             * 
             * @see ISS-002 Security Hardening
             */
            try {
              if (authChannel) {
                authChannel.postMessage({ type: 'LOGOUT', timestamp: Date.now() });
                if (import.meta.env.DEV) {
                  console.log('[Auth] Broadcast logout to other tabs');
                }
              }
            } catch (error) {
              // Non-critical - just log in dev
              if (import.meta.env.DEV) {
                console.warn('[Auth] Failed to broadcast logout:', error);
              }
            }

            // Note: Audit logging removed to avoid Immer race conditions
            // Server-side logout tracking should be handled by the API
          },
          
          /**
           * Clear current error state
           */
          clearError: () => {
            set((state) => {
              state.error = null;
            });
          },

          /**
           * Passwordless Auth Step 1: Request Passcode
           * 
           * Sends a one-time passcode to the user's email via Resend.
           * In demo mode, passcode is generated locally and logged to console.
           * 
           * @param email - User's email address
           * @returns Promise<boolean> - true if passcode sent successfully
           */
          requestPasscode: async (email: string): Promise<boolean> => {
            set((state) => {
              state.status = 'loading';
              state.error = null;
            });

            try {
              // Import email service dynamically to avoid circular deps
              const { requestPasscodeEmail, storeDemoPasscode, generatePasscode } = await import('../api/email');
              
              // Send passcode via email API or demo mode
              const result = await requestPasscodeEmail(email);
              
              if (!result.success) {
                const authError: AuthError = {
                  code: 'passcode_request_failed',
                  message: result.error || 'Failed to send passcode',
                };
                set((state) => {
                  state.status = 'unauthenticated';
                  state.error = authError;
                });
                return false;
              }
              
              // In demo mode, the passcode is returned and displayed
              // In production, the passcode is only sent to the email
              set((state) => {
                state.status = 'awaiting_passcode';
                state.demoPasscode = result.passcode || null;
              });
              
              if (result.passcode && import.meta.env.DEV) {
                console.log('[Auth] Demo mode - passcode:', result.passcode);
              }
              
              return true;
            } catch (error) {
              const authError: AuthError = {
                code: 'passcode_request_failed',
                message: error instanceof Error ? error.message : 'Failed to send passcode',
              };
              set((state) => {
                state.status = 'unauthenticated';
                state.error = authError;
              });
              return false;
            }
          },

          /**
           * Passwordless Auth Step 2: Verify Passcode
           * 
           * Verifies the passcode and completes authentication.
           * Updates: loginCount, lastLoginAt
           * 
           * @param email - User's email address
           * @param passcode - 6-digit passcode from email
           * @returns Promise<boolean> - true if verified and logged in
           * 
           * SECURITY FIXES (ISS-002, ISS-003, ISS-009):
           * - Demo mode is environment-guarded (not in production unless explicitly enabled)
           * - Demo user roles are stripped from production builds
           * - Console logs are gated behind DEV mode
           */
          verifyPasscode: async (email: string, passcode: string): Promise<boolean> => {
            set((state) => {
              state.status = 'loading';
              state.error = null;
            });

            try {
              // Import email service for verification
              const { verifyDemoPasscode, verifyPasscodeAPI } = await import('../api/email');
              const { isDemoModeEnabled } = await import('../utils/environment');
              
              // Check demo mode - SECURITY: Only enabled in development or when explicitly set
              const DEMO_MODE = isDemoModeEnabled();
              const IS_DEV = import.meta.env.DEV;
              
              let verifyResult: { success: boolean; token?: string; user?: { email: string; role: string }; error?: string };
              
              // ALWAYS try real API first (passcodes are sent via email)
              if (IS_DEV) {
                console.log('[Auth] Verifying passcode via API...');
              }
              verifyResult = await verifyPasscodeAPI(email, passcode);
              
              /**
               * ISS-002 FIX: Fail-Closed Security Model
               * 
               * Demo fallback is ONLY allowed when:
               * 1. API call explicitly fails (not just returns success:false)
               * 2. DEMO_MODE is explicitly enabled via environment variable
               * 3. We're in development mode
               * 
               * In production, if API fails, user must retry - NO fallback to demo auth
               */
              if (!verifyResult.success && DEMO_MODE && IS_DEV) {
                // API failed AND demo mode enabled AND in development - try demo fallback
                if (IS_DEV) {
                  console.log('[Auth] API verification failed, trying demo fallback (DEV only)...');
                }
                const isValid = verifyDemoPasscode(email, passcode);
                if (isValid) {
                  if (IS_DEV) {
                    console.log('[Auth] ✅ Demo passcode verified (DEV mode)');
                  }
                  verifyResult = { success: true };
                }
              }
              
              if (!verifyResult.success) {
                const authError: AuthError = {
                  code: 'invalid_passcode',
                  message: verifyResult.error || 'Invalid or expired passcode. Please try again.',
                };
                set((state) => {
                  state.status = 'awaiting_passcode';
                  state.error = authError;
                });
                return false;
              }
              
              /**
               * ROLE DETERMINATION
               * 
               * SECURITY NOTE: Frontend role assignment is ADVISORY ONLY.
               * The backend MUST enforce all role-based access control.
               * This frontend logic is purely for UX routing purposes.
               * 
               * Priority:
               * 1. API-provided role (authoritative source - ALWAYS checked first)
               * 2. Environment-configured role overrides (VITE_USER_ROLE_OVERRIDES)
               * 3. Demo role mapping (ONLY in development mode)
               * 4. Default to 'viewer' (most restrictive)
               */
              
              // Import environment-based role overrides (production-safe)
              const { getRoleOverrideForEmail } = await import('../utils/environment');
              
              // ISS-003 FIX: Demo user roles only in development
              // In production builds, this will be an empty object (tree-shaken if unused)
              const DEMO_USER_ROLES: Record<string, UserRole> = IS_DEV ? {
                // Admin users - access to admin portal
                'admin1@cyberraptor.ai': 'admin',
                'admin2@cyberraptor.ai': 'admin',
                // Operator users - access to Operator Portal (NOT dashboard!)
                'ken@cyberraptor.ai': 'operator',
                'keith@cyberraptor.ai': 'operator',
              } : {};
              
              const emailLower = email.toLowerCase();
              let role: UserRole = 'viewer';
              
              // Role mapping for backend role strings
              const roleMapping: Record<string, UserRole> = {
                'admin': 'admin',
                'super_admin': 'admin',
                'senior_analyst': 'senior_analyst',
                'security_analyst': 'analyst',
                'analyst': 'analyst',
                'operator': 'operator',
                'viewer': 'viewer',
              };
              
              /**
               * ROLE DETERMINATION (Priority Order):
               * 1. Environment variable override (VITE_USER_ROLE_OVERRIDES) - HIGHEST PRIORITY
               *    When explicitly configured, this takes precedence over API
               * 2. API-provided role (backend default)
               * 3. Demo role mapping (dev-only for testing)
               * 4. Default to 'viewer' (most restrictive)
               * 
               * RATIONALE: If an admin explicitly configures a role override via env var,
               * they want it to take effect regardless of what the backend returns.
               * This allows frontend UX routing to be controlled independently.
               * 
               * SECURITY NOTE: This is UX-level only. Backend MUST still enforce RBAC.
               */
              
              // Priority 1: Check environment variable override FIRST (explicit admin config)
              const envRoleOverride = getRoleOverrideForEmail(emailLower);
              if (envRoleOverride && roleMapping[envRoleOverride]) {
                role = roleMapping[envRoleOverride];
                console.log(`[Auth] Using env override role for ${emailLower}: ${role}`);
              } else if (verifyResult.user?.role) {
                // Priority 2: API-provided role (backend default)
                const rawRole = verifyResult.user.role;
                const backendRole = typeof rawRole === 'string' ? rawRole.toLowerCase() : '';
                role = roleMapping[backendRole] || 'viewer';
                if (IS_DEV) {
                  console.log(`[Auth] Using API role for ${emailLower}: ${backendRole} -> ${role}`);
                }
                if (!roleMapping[backendRole] && IS_DEV) {
                  console.warn(`[Auth] Unknown backend role "${backendRole}", defaulting to viewer`);
                }
              } else if (DEMO_USER_ROLES[emailLower]) {
                // Priority 3: Demo roles (dev-only)
                role = DEMO_USER_ROLES[emailLower];
                if (IS_DEV) {
                  console.log(`[Auth] Using demo role for ${emailLower}: ${role}`);
                }
              } else {
                // Priority 4: Default to viewer
                if (IS_DEV) {
                  console.log(`[Auth] No role found for ${emailLower}, defaulting to viewer`);
                }
              }
              
              // Create user with login tracking
              const now = new Date();
              const userId = crypto.randomUUID();
              
              /**
               * LOGIN TRACKING (SIMULATION)
               * 
               * In production, loginCount and lastLoginAt would be:
               * 1. Updated in the BACKEND database during verifyPasscode API call
               * 2. Returned as part of the User object in the API response
               * 3. NOT stored in localStorage (which is device-specific)
               * 
               * This localStorage implementation is for DEMONSTRATION ONLY
               * to show the data model. Real implementations MUST use backend storage.
               */
              const storedLoginCount = parseInt(localStorage.getItem(`login_count_${email}`) || '0', 10);
              const newLoginCount = storedLoginCount + 1;
              
              // Store updated login count (DEMO ONLY - production uses backend)
              localStorage.setItem(`login_count_${email}`, String(newLoginCount));
              localStorage.setItem(`last_login_${email}`, now.toISOString());
              
              const emailLocalPart = email.split('@')[0] ?? 'User';
              const formattedName = emailLocalPart.replace(/[._]/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              
              /**
               * RUNTIME USER VALIDATION (Security Enhancement)
               * 
               * Before setting authentication state, validate the user object
               * to ensure data integrity. This catches:
               * - Malformed API responses
               * - Tampered data
               * - Type mismatches that would corrupt state
               * 
               * @see validateUser() for validation logic
               */
              const userCandidate = {
                id: userId,
                name: formattedName,
                email: email,
                role: role,
                createdAt: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000), // 90 days ago
                mfaEnabled: false,
                lastLoginAt: now,
                loginCount: newLoginCount,
              };
              
              const validation = validateUser(userCandidate);
              if (!validation.valid) {
                // Critical: User data is malformed - do not authenticate
                const authError: AuthError = {
                  code: 'validation_failed',
                  message: IS_DEV 
                    ? `User validation failed: ${validation.errors.join(', ')}`
                    : 'Authentication data validation failed. Please try again.',
                };
                if (IS_DEV) {
                  console.error('[Auth] User validation failed:', validation.errors);
                }
                set((state) => {
                  state.status = 'awaiting_passcode';
                  state.error = authError;
                });
                return false;
              }
              
              const authenticatedUser = validation.data;

              // Use JWT from API response if available, otherwise generate local token
              const accessToken = verifyResult.token || `passcode_auth_token_${Date.now()}_${crypto.randomUUID().slice(0, 8)}`;
              
              const newToken: AuthToken = {
                accessToken: accessToken,
                refreshToken: `passcode_refresh_token_${Date.now()}_${crypto.randomUUID().slice(0, 8)}`,
                tokenType: 'Bearer',
                expiresIn: 86400, // 24 hours (matching JWT expiry)
                issuedAt: now,
              };

              // Update state with authenticated user
              set((state) => {
                state.user = authenticatedUser;
                state.token = newToken;
                state.status = 'authenticated';
                state.permissions = getPermissionsForRole(role);
                state.sessionStartedAt = now;
                state.lastActivityAt = now;
              });

              // Start token refresh timer
              get().startTokenRefreshTimer();

              // Log successful login
              get().logAuditEvent(
                'dashboard:view',
                true,
                'login',
                'session',
                userId,
                { 
                  method: 'passcode',
                  loginCount: newLoginCount,
                  email: email,
                }
              );

              if (IS_DEV) {
                console.log('[Auth] Login successful:', {
                  email,
                  role,
                  loginCount: newLoginCount,
                  lastLogin: now.toISOString(),
                });
              }

              return true;
            } catch (error) {
              const authError: AuthError = {
                code: 'verification_failed',
                message: error instanceof Error ? error.message : 'Verification failed',
              };
              set((state) => {
                state.status = 'awaiting_passcode';
                state.error = authError;
              });
              return false;
            }
          },

          /**
           * Token Refresh with Singleton Promise Pattern
           * 
           * Prevents race conditions when multiple concurrent 401s trigger refresh.
           * All concurrent callers share the same Promise, ensuring only ONE refresh
           * request is made to the server.
           * 
           * @returns Promise<boolean> - true if refresh succeeded, false otherwise
           */
          refreshToken: (() => {
            // Closure to hold the singleton promise
            let refreshPromise: Promise<boolean> | null = null;

            return async (): Promise<boolean> => {
              // If a refresh is already in progress, return the existing promise
              // This is the key to preventing race conditions!
              if (refreshPromise) {
                return refreshPromise;
              }

              const { token, user } = get();

              if (!token?.refreshToken) {
                set((state) => {
                  state.status = 'session_expired';
                });
                return false;
              }

              set((state) => {
                state.isRefreshing = true;
              });

              // Create the refresh promise and store it
              refreshPromise = (async () => {
                try {
                  // API call would go here
                  // const response = await authAPI.refreshToken(token.refreshToken);

                  // Simulated response
                  const newToken: AuthToken = {
                    accessToken: 'new_simulated_jwt_token_' + Date.now(),
                    refreshToken: 'new_simulated_refresh_token_' + Date.now(),
                    tokenType: 'Bearer',
                    expiresIn: 3600,
                    issuedAt: new Date(),
                  };

                  set((state) => {
                    state.token = newToken;
                    state.isRefreshing = false;
                  });

                  // Log token refresh
                  if (user) {
                    get().logAuditEvent(
                      'dashboard:view',
                      true,
                      'token_refresh',
                      'session',
                      user.id
                    );
                  }

                  return true;
                } catch {
                  set((state) => {
                    state.isRefreshing = false;
                    state.status = 'session_expired';
                  });
                  return false;
                } finally {
                  // Clear the singleton promise after completion
                  refreshPromise = null;
                }
              })();

              return refreshPromise;
            };
          })(),

          submitMfaCode: async (code: string): Promise<boolean> => {
            set((state) => {
              state.status = 'loading';
            });

            try {
              // API call would verify MFA code
              // const response = await authAPI.verifyMfa(code);

              // For now, accept any 6-digit code in development
              if (!/^\d{6}$/.test(code)) {
                throw new Error('Invalid MFA code format');
              }

              set((state) => {
                state.status = 'authenticated';
                state.showMfaDialog = false;
              });

              return true;
            } catch {
              set((state) => {
                state.error = {
                  code: 'mfa_invalid',
                  message: 'Invalid MFA code',
                };
              });
              return false;
            }
          },

          // ----------------------------------------------------------------
          // Session Management
          // ----------------------------------------------------------------

          setUser: (user: User | null) => {
            set((state) => {
              state.user = user;
              if (user) {
                state.permissions = getPermissionsForRole(user.role);
              } else {
                state.permissions = [];
              }
              state.permissionCache.clear();
            });
          },

          setToken: (token: AuthToken | null) => {
            set((state) => {
              state.token = token;
            });
          },

          setStatus: (status: AuthStatus) => {
            set((state) => {
              state.status = status;
            });
          },

          setError: (error: AuthError | null) => {
            set((state) => {
              state.error = error;
            });
          },

          setHasHydrated: (hasHydrated: boolean) => {
            set((state) => {
              state.hasHydrated = hasHydrated;
              // If we hydrated and still have no user, make the status explicit.
              if (hasHydrated && !state.user && state.status === 'loading') {
                state.status = 'unauthenticated';
              }
            });
          },

          updateLastActivity: () => {
            set((state) => {
              state.lastActivityAt = new Date();
            });
          },

          checkSessionExpiry: (): boolean => {
            const { lastActivityAt, sessionTimeoutMs, status } = get();

            if (status !== 'authenticated') {return true;}
            if (!lastActivityAt) {return true;}

            const elapsed = Date.now() - lastActivityAt.getTime();
            if (elapsed > sessionTimeoutMs) {
              set((state) => {
                state.status = 'session_expired';
              });
              return true;
            }

            return false;
          },

          startTokenRefreshTimer: () => {
            const { token, stopTokenRefreshTimer, refreshToken } = get();

            // Stop any existing timer
            stopTokenRefreshTimer();

            if (!token) {return;}

            // Refresh token 5 minutes before expiry
            const refreshInterval = Math.max(
              (token.expiresIn - 300) * 1000,
              60000 // Minimum 1 minute
            );

            const intervalId = window.setInterval(() => {
              refreshToken();
            }, refreshInterval);

            set((state) => {
              state.tokenRefreshIntervalId = intervalId;
            });
          },

          stopTokenRefreshTimer: () => {
            const { tokenRefreshIntervalId } = get();
            if (tokenRefreshIntervalId) {
              clearInterval(tokenRefreshIntervalId);
              set((state) => {
                state.tokenRefreshIntervalId = null;
              });
            }
          },

          // ----------------------------------------------------------------
          // Permission Checking
          // ----------------------------------------------------------------

          hasPermission: (permission: Permission): boolean => {
            const { permissions, permissionCache, user } = get();

            if (!user) {return false;}

            // Check cache first
            const cacheKey = permission;
            if (permissionCache.has(cacheKey)) {
              return permissionCache.get(cacheKey)!;
            }

            const result = permissions.includes(permission);

            // Cache the result
            set((state) => {
              state.permissionCache.set(cacheKey, result);
            });

            return result;
          },

          hasAllPermissions: (requiredPermissions: Permission[]): boolean => {
            const { permissions, user } = get();
            if (!user) {return false;}
            return hasAllPermissions(permissions, requiredPermissions);
          },

          hasAnyPermission: (requiredPermissions: Permission[]): boolean => {
            const { permissions, user } = get();
            if (!user) {return false;}
            return hasAnyPermission(permissions, requiredPermissions);
          },

          hasMinimumRole: (role: UserRole): boolean => {
            const { user } = get();
            if (!user) {return false;}
            return hasMinimumRole(user.role, role);
          },

          checkPermission: (
            permission: Permission,
            resourceId?: string
          ): boolean => {
            const { hasPermission, logAuditEvent, user } = get();

            const allowed = hasPermission(permission);

            // Log the permission check for audit
            if (user) {
              const parts = permission.split(':');
              const category = parts[0] || 'unknown';
              const action = parts[1] || 'view';
              logAuditEvent(
                permission,
                allowed,
                action,
                category,
                resourceId
              );
            }

            return allowed;
          },

          clearPermissionCache: () => {
            set((state) => {
              state.permissionCache.clear();
            });
          },

          // ----------------------------------------------------------------
          // Audit Logging
          // ----------------------------------------------------------------

          logAuditEvent: (
            permission: Permission,
            allowed: boolean,
            action: string,
            resourceType: string,
            resourceId?: string,
            metadata?: Record<string, unknown>
          ) => {
            const { user, token } = get();

            if (!user) {return;}

            const entry: AuditLogEntry = {
              id: crypto.randomUUID(),
              userId: user.id,
              userRole: user.role,
              permission,
              allowed,
              action,
              resourceType,
              resourceId,
              ipAddress: 'client', // Would be from request context
              userAgent: navigator.userAgent,
              timestamp: new Date(),
              metadata,
              sessionId: token?.accessToken.slice(-16) || 'unknown',
            };

            set((state) => {
              state.auditQueue.push(entry);

              // Auto-flush if queue is large
              if (state.auditQueue.length >= 50) {
                // Flush would happen here
              }
            });
          },

          flushAuditQueue: async () => {
            const { auditQueue } = get();

            if (auditQueue.length === 0) {return;}

            try {
              // IMPORTANT: This is for TELEMETRY only, not compliance.
              // Server-side audit logging (403 responses) is the compliance trail.
              // These client-side logs are for UX analytics and debugging.
              // await telemetryAPI.batchLog(auditQueue);

              // For now, log to console in development
              if (import.meta.env.DEV) {
                console.log('[Telemetry] Flushing', auditQueue.length, 'permission events');
              }

              set((state) => {
                state.auditQueue = [];
              });
            } catch (error) {
              // Telemetry failures are non-critical - don't block user
              console.warn('[Telemetry] Failed to flush queue (non-critical):', error);
            }
          },

          // ----------------------------------------------------------------
          // Utilities
          // ----------------------------------------------------------------

          getDisplayRole: (): string => {
            const { user } = get();
            if (!user) {return 'Guest';}

            const roleDisplayNames: Record<UserRole, string> = {
              viewer: 'Viewer',
              operator: 'Operator',
              analyst: 'Analyst',
              security_analyst: 'Security Analyst',
              senior_analyst: 'Senior Analyst',
              admin: 'Administrator',
              super_admin: 'Super Administrator',
            };

            return roleDisplayNames[user.role];
          },

          canAccessRoute: (
            requiredPermissions?: Permission[],
            requiredRole?: UserRole
          ): boolean => {
            const { user, permissions } = get();

            if (!user) {return false;}

            // Check role requirement
            if (requiredRole) {
              if (ROLE_HIERARCHY[user.role] < ROLE_HIERARCHY[requiredRole]) {
                return false;
              }
            }

            // Check permissions
            if (requiredPermissions && requiredPermissions.length > 0) {
              return hasAllPermissions(permissions, requiredPermissions);
            }

            return true;
          },

          reset: () => {
            get().stopTokenRefreshTimer();
            set(() => ({ ...initialState }));
          },
        }))
      ),
      {
        name: 'raptor-auth-store',
        partialize: (state) => ({
          user: state.user,
          permissions: state.permissions,
          status: state.status,
          sessionStartedAt: state.sessionStartedAt,
        }),
        onRehydrateStorage: () => (state) => {
          // Mark hydration complete ASAP (also triggers a persist write with the
          // partialized state, scrubbing sensitive token fields from storage).
          state?.setHasHydrated(true);

          // Defensive: if a token was injected into storage (tests/dev),
          // immediately remove refresh tokens from persisted state.
          if (state?.token?.refreshToken) {
            state.setToken({ ...state.token, refreshToken: '' });
          }
          
          /**
           * CRITICAL FIX: Set status to 'unauthenticated' when no user exists
           * 
           * Without this, new users (no localStorage data) will see an endless
           * "Verifying authorization..." spinner because status stays at 'loading'
           * after hydration.
           */
          if (!state?.user) {
            console.log('[Auth] No stored user - clearing session');
            state?.setStatus('unauthenticated');
            state?.setToken(null);
            return;
          }
          
          /**
           * AGGRESSIVE SESSION VALIDATION
           * 
           * If status claims authenticated but data is incomplete/invalid,
           * force logout to prevent stale session issues.
           */
          if (state?.status === 'authenticated' || state?.status === 'loading') {
            // Must have valid user with role
            if (!state.user?.role || !state.user?.email) {
              console.log('[Auth] Invalid user data - forcing logout');
              state.setStatus('unauthenticated');
              state.setUser(null);
              state.setToken(null);
              return;
            }
            
            // Must have sessionStartedAt for session age tracking
            if (!state.sessionStartedAt) {
              console.log('[Auth] Missing session timestamp - forcing logout');
              state.setStatus('unauthenticated');
              state.setUser(null);
              state.setToken(null);
              return;
            }
          }
          
          /**
           * SESSION EXPIRY VALIDATION
           * 
           * SECURITY: Persisted auth state must be validated on rehydration.
           * If the session is too old (>24 hours), force re-authentication.
           * This prevents "forever authenticated" state from localStorage.
           * 
           * In production with a real backend, this would also verify
           * the token with the server. For demo/frontend-only mode,
           * we use time-based expiry.
           */
          if (state?.status === 'authenticated') {
            // CONFIG: Allow 10 seconds of clock drift to prevent false positives on NTP sync
            const CLOCK_SKEW_BUFFER_MS = 10000;
            const MAX_SESSION_HOURS = Math.round(MAX_SESSION_AGE_MS / 1000 / 60 / 60);
            
            // Helper to clear session state - DRY principle
            // Only log detailed reasons in non-production for security
            const clearSession = (reason: string) => {
              if (import.meta.env.DEV) {
                console.warn(`[Auth] ${reason}`);
              } else {
                // Production: minimal logging to avoid exposing security details
                console.log('[Auth] Session invalidated - re-authentication required');
              }
              state.setStatus('unauthenticated');
              state.setUser(null);
              state.setToken(null);
            };
            
            // If sessionStartedAt is missing, the session is from an older version
            if (!state.sessionStartedAt) {
              clearSession('Session missing start time - forcing re-authentication');
              return;
            }
            
            // Parse the session start time with validation
            const sessionStartedAt = new Date(state.sessionStartedAt);
            const sessionTimestamp = sessionStartedAt.getTime();
            
            // SECURITY: Validate parsed date - protect against corrupted/tampered storage
            if (Number.isNaN(sessionTimestamp)) {
              clearSession('Session has invalid start time (corrupted) - forcing re-authentication');
              return;
            }
            
            // Calculate session age
            const sessionAge = Date.now() - sessionTimestamp;
            
            // SECURITY: Check for future timestamps with clock skew tolerance
            // Allows minor NTP sync drift (10s) but blocks manipulation
            if (sessionAge < -CLOCK_SKEW_BUFFER_MS) {
              clearSession('Session start time is in the future (clock skew/manipulation detected) - forcing re-authentication');
              return;
            }
            
            // Check if session has expired
            const sessionHours = Math.round(sessionAge / 1000 / 60 / 60);
            if (sessionAge > MAX_SESSION_AGE_MS) {
              clearSession(`Session expired (${sessionHours}h old > ${MAX_SESSION_HOURS}h limit) - requiring re-authentication`);
            } else if (import.meta.env.DEV) {
              // Only log session age in development
              console.log('[Auth] Session still valid, age:', Math.round(sessionAge / 1000 / 60), 'minutes');
            }
          }
        },
      }
    ),
    { 
      name: 'auth-store',
      // SECURITY: Only enable DevTools in development mode
      // This prevents credential exposure in production via browser extensions
      enabled: import.meta.env.DEV,
      // SECURITY: Sanitize sensitive data in DevTools
      // Prevents tokens and PII from being logged in browser extension history
      // NOTE: Mask based on KEY name (not value) to be robust against token format changes
      serialize: {
        replacer: (key: string, value: unknown) => {
          // Mask sensitive keys regardless of value format
          // This is more robust than checking value prefix (in case token format changes)
          const SENSITIVE_KEYS = ['token', 'accessToken', 'refreshToken', 'demoPasscode'];
          if (SENSITIVE_KEYS.includes(key)) {
            return '***MASKED***';
          }
          // Backup: also mask values that look like tokens (defense in depth)
          if (typeof value === 'string' && (
            value.startsWith('passcode_auth_token_') ||
            value.startsWith('passcode_refresh_token_') ||
            value.startsWith('ey') // JWT format
          )) {
            return '***MASKED_TOKEN***';
          }
          return value;
        },
      },
    }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectUser = (state: AuthState) => state.user;
export const selectToken = (state: AuthState) => state.token;
export const selectStatus = (state: AuthState) => state.status;
export const selectError = (state: AuthState) => state.error;
export const selectHasHydrated = (state: AuthState) => state.hasHydrated;
export const selectPermissions = (state: AuthState) => state.permissions;
export const selectIsAuthenticated = (state: AuthState) =>
  state.status === 'authenticated';
export const selectIsLoading = (state: AuthState) => state.status === 'loading';
export const selectIsRefreshing = (state: AuthState) => state.isRefreshing;
export const selectShowMfaDialog = (state: AuthState) => state.showMfaDialog;
export const selectUserRole = (state: AuthState) => state.user?.role;
export const selectSessionStartedAt = (state: AuthState) => state.sessionStartedAt;
export const selectLastActivityAt = (state: AuthState) => state.lastActivityAt;

// ============================================================================
// Cross-Tab Logout Listener
// ============================================================================

/**
 * Listen for logout events from other tabs
 * 
 * SECURITY: When user logs out in another tab, immediately clear
 * authentication state in this tab. This prevents:
 * 1. Zombie sessions on shared computers
 * 2. Actions being performed on dead sessions
 * 3. Confusion about authentication state
 * 
 * @see ISS-002 Security Hardening
 */
if (authChannel) {
  authChannel.onmessage = (event: MessageEvent) => {
    // SECURITY: Validate event structure to prevent processing garbage data
    if (!event.data || typeof event.data !== 'object') {
      if (import.meta.env.DEV) {
        console.warn('[Auth] Received malformed broadcast message');
      }
      return;
    }
    
    if (event.data.type === 'LOGOUT') {
      const state = useAuthStore.getState();
      
      // Only logout if currently authenticated (prevent unnecessary re-renders)
      if (state.status === 'authenticated') {
        if (import.meta.env.DEV) {
          console.log('[Auth] Received logout broadcast from another tab');
        }
        
        // Stop any timers
        state.stopTokenRefreshTimer();
        
        // Clear state without broadcasting (to avoid infinite loop)
        useAuthStore.setState({
          user: null,
          token: null,
          status: 'unauthenticated',
          error: null,
          permissions: [],
          permissionCache: new Map(),
          sessionStartedAt: null,
          lastActivityAt: null,
          auditQueue: [],
        });
        
        // Optionally redirect to login page
        // This will be handled by ProtectedRoute detecting unauthenticated status
      }
    }
  };
}

